import { Controller } from "@hotwired/stimulus"
import airports from "../data/airports.json"

// Local gravity = WGS-84 normal gravity on the ellipsoid minus the free-air
// term for elevation (3.086 µm/s² per metre) — the model named in
// docs/aircraft_weighing_spec_newest.md and the one weights-and-measures
// authorities prescribe for locating a scale's gravity zone.
const FREE_AIR_PER_M = 3.086e-6
const MAX_RESULTS = 8

// airports.json is generated by `bin/rails airports:build` (see
// lib/tasks/airports.rake): ~5 000 large and medium airports, large first.
// Lower-cased once here so a keystroke does not re-fold 20 000 strings.
const INDEX = airports.map(airport => ({
  airport,
  icao: airport.icao.toLowerCase(),
  iata: airport.iata.toLowerCase(),
  name: airport.name.toLowerCase(),
  city: airport.city.toLowerCase()
}))

const escapeHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;")

export default class extends Controller {
  static targets = ["input", "list", "icao", "iata", "gravity", "gravitySource", "latitude", "elevation"]

  connect() {
    this.selectedIndex = -1
    this.listTarget.id ||= `airport-results-${Math.random().toString(36).slice(2)}`
    this.listTarget.setAttribute("role", "listbox")
    this.inputTarget.setAttribute("role", "combobox")
    this.inputTarget.setAttribute("aria-autocomplete", "list")
    this.inputTarget.setAttribute("aria-controls", this.listTarget.id)
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside)
  }

  search() {
    const query = this.inputTarget.value.trim().toLowerCase()
    if (this.selectedAirport && !this.matchesSelectedAirport(query)) {
      this.clearDependentFields()
    }
    if (query.length < 2) {
      this.hide()
      return
    }

    this.selectedIndex = -1
    this.showResults(this.rank(query))
  }

  // Exact code first, then a name or city word starting with the query, then
  // a code prefix, then any substring. With 5 000 airports a bare substring
  // filter would list random hits before Manchester for "man". Within a tier
  // the dataset order holds: large airports before medium ones.
  rank(query) {
    const tiered = []
    for (const entry of INDEX) {
      const tier = this.matchTier(entry, query)
      if (tier >= 0) tiered.push([tier, entry.airport])
    }
    return tiered.sort((a, b) => a[0] - b[0]).slice(0, MAX_RESULTS).map(([, airport]) => airport)
  }

  matchTier({ icao, iata, name, city }, q) {
    if (icao === q || (iata && iata === q)) return 0
    if (name.startsWith(q) || city.startsWith(q) || name.includes(` ${q}`) || city.includes(` ${q}`)) return 1
    if (icao.startsWith(q) || iata.startsWith(q)) return 2
    if (name.includes(q) || city.includes(q) || icao.includes(q) || iata.includes(q)) return 3
    return -1
  }

  matchesSelectedAirport(query) {
    if (!this.selectedAirport) return false
    const { icao, city, label } = this.selectedAirport
    return query === icao.toLowerCase() ||
      query === (city || "").toLowerCase() ||
      query === (label || "").toLowerCase()
  }

  // A hand-edited gravity value loses its WGS-84 provenance: the source
  // must go blank so the record no longer claims a location-derived value
  // (WeighingSession#location_derived_gravity?).
  gravityEdited() {
    if (this.hasGravitySourceTarget) this.gravitySourceTarget.value = ""
  }

  clearDependentFields() {
    this.selectedAirport = null
    if (this.hasIcaoTarget) this.icaoTarget.value = ""
    if (this.hasIataTarget) this.iataTarget.value = ""
    this.clearLocationDerivedFields()
  }

  clearLocationDerivedFields() {
    if (this.hasLatitudeTarget) this.latitudeTarget.value = ""
    if (this.hasElevationTarget) this.elevationTarget.value = ""
    if (this.hasGravityTarget) this.gravityTarget.value = ""
    if (this.hasGravitySourceTarget) this.gravitySourceTarget.value = ""
  }

  showResults(results) {
    if (results.length === 0) {
      this.hide()
      return
    }

    this.listTarget.innerHTML = results.map((a, i) => {
      const codes = [a.iata, a.icao].filter(Boolean).join(" / ")
      const detail = [a.city, codes].filter(Boolean).map(escapeHtml).join(" &mdash; ")
      return `<button type="button" class="airport-option" role="option" id="${this.listTarget.id}-option-${i}" aria-selected="false" data-index="${i}" data-icao="${escapeHtml(a.icao)}" data-iata="${escapeHtml(a.iata)}" data-lat="${a.lat ?? ""}" data-elev="${a.elev ?? ""}" data-city="${escapeHtml(a.city)}" data-name="${escapeHtml(a.name)}" data-action="click->airport-search#pick">
        <span class="airport-code">${escapeHtml(a.iata || a.icao)}</span>
        <span class="airport-detail">
          <span class="airport-name">${escapeHtml(a.name)}</span>
          <span class="airport-city">${detail}</span>
        </span>
      </button>`
    }).join("")

    this.listTarget.classList.add("active")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  pick(event) {
    event.preventDefault()
    this.selectAirport(this.airportFromOption(event.currentTarget))
  }

  airportFromOption(option) {
    const { icao, iata, name, city, lat, elev } = option.dataset
    return {
      icao, iata, name, city,
      lat: parseFloat(lat),
      elev: elev === "" || elev === undefined ? null : parseInt(elev, 10)
    }
  }

  selectAirport({ icao, iata, name, city, lat, elev }) {
    // The stored location is the full airport name plus both codes, IATA
    // (the code the team primarily uses) first: "Amsterdam Airport Schiphol (AMS / EHAM)".
    const codes = [iata, icao].filter(Boolean).join(" / ")
    const label = `${name || city || icao}${codes ? ` (${codes})` : ""}`
    this.selectedAirport = { icao, city: city || icao, label }
    this.inputTarget.value = label
    if (this.hasIcaoTarget) this.icaoTarget.value = icao
    if (this.hasIataTarget) this.iataTarget.value = iata || ""
    this.hide()
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))

    if (isNaN(lat)) {
      this.clearLocationDerivedFields()
      return
    }

    // 61 of the ~5 000 airports have no elevation upstream: gravity is then
    // computed at sea level and the source line says so, so the technician
    // can see it and override with a reason if the field is anywhere high.
    const elevationKnown = Number.isInteger(elev)
    const elevation = elevationKnown ? elev : 0
    if (this.hasLatitudeTarget) this.latitudeTarget.value = lat
    if (this.hasElevationTarget) this.elevationTarget.value = elevationKnown ? elev : ""
    if (this.hasGravityTarget) this.gravityTarget.value = this.computeGravity(lat, elevation)
    if (this.hasGravitySourceTarget) {
      const elevationNote = elevationKnown ? `elev ${elev} m` : "elev unknown"
      this.gravitySourceTarget.value = `WGS-84 formula (${icao}, lat ${lat.toFixed(2)}°, ${elevationNote})`
    }
  }

  // International Gravity Formula (GRS80 / WGS-84) with free-air correction:
  // g(φ, h) = 9.780327 × (1 + 0.0053024·sin²φ − 0.0000058·sin²2φ) − 3.086e-6·h
  computeGravity(latDeg, elevationM = 0) {
    const phi = latDeg * Math.PI / 180
    const sinPhi = Math.sin(phi)
    const sin2Phi = Math.sin(2 * phi)
    const g0 = 9.780327 * (1 + 0.0053024 * sinPhi * sinPhi - 0.0000058 * sin2Phi * sin2Phi)
    return (g0 - FREE_AIR_PER_M * elevationM).toFixed(5)
  }

  navigate(event) {
    const options = this.listTarget.querySelectorAll(".airport-option")
    if (!options.length || !this.listTarget.classList.contains("active")) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.selectedIndex = Math.min(this.selectedIndex + 1, options.length - 1)
      this.highlightOption(options)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
      this.highlightOption(options)
    } else if (event.key === "Enter" && this.selectedIndex >= 0) {
      event.preventDefault()
      this.selectAirport(this.airportFromOption(options[this.selectedIndex]))
    } else if (event.key === "Escape") {
      this.hide()
    }
  }

  highlightOption(options) {
    options.forEach((o, i) => {
      const selected = i === this.selectedIndex
      o.classList.toggle("highlighted", selected)
      o.setAttribute("aria-selected", selected.toString())
    })
    if (options[this.selectedIndex]) {
      this.inputTarget.setAttribute("aria-activedescendant", options[this.selectedIndex].id)
      options[this.selectedIndex].scrollIntoView({ block: "nearest" })
    }
  }

  hide() {
    this.listTarget.classList.remove("active")
    this.listTarget.innerHTML = ""
    this.selectedIndex = -1
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hide()
    }
  }
}
